/* eslint-disable no-case-declarations */
/**
 * For reference: We have copied the code from https://github.com/mrdoob/three.js/blob/dev/examples/jsm/loaders/BVHLoader.js
 * And have applied the following changes:
 * - Implement a bvh cache so that files are loaded into memory and don't need to be refetched
 * - Implemented a rotationOffsets prop to fix arm and leg space for characters with different body types
 * - Convert code to typescript
 * - Round the frame time to the nearest frame rate (e.g. frame rate of 24 instead of 23.982...)
 * - Sanitize joint names with sanitizeBindingNodeName (same as PropertyBinding patch); raw BVH name on userData.name when it differs
 * - clip.userData.primaryPositionTrackName: the *.position track with the largest path length (see computePrimaryPositionTrackNameFromTracks)
 * - clip.userData.morphJoints: Set of (sanitized) joint names whose BVH channels are exactly ["Xposition"]; these joints drive morph target weights, not scene transforms
 * - clip.userData.morphTracks: KeyframeTrack[] of the original *.position tracks for those morph driver joints; preserved before filterTracks drops them so a downstream modifier can rewrite them as morphTargetInfluences tracks
 */
import {
  AnimationClip,
  Bone,
  FileLoader,
  KeyframeTrack,
  Loader,
  LoadingManager,
  Quaternion,
  Skeleton,
  KeyframeTrack as ThreeKeyframeTrack,
  Vector3,
} from 'three';

import { RotationOffsets } from '@cartwhl/common/types';

import {
  computePrimaryPositionTrackNameFromTracks,
  MHR_BODY_WORLD_POSITION_TRACK_NAME,
  isMorphTrack,
} from '../rootTranslationTrack';
import { sanitizeBindingNodeName } from '../../threejs/patchSanitizeNodeName';

import { QuaternionKeyframeTrack } from './QuaternionKeyframeTrack';
import { VectorKeyframeTrack } from './VectorKeyframeTrack';

interface BVHNode {
  name: string;
  type: string;
  frames: BVHFrame[];
  offset?: Vector3;
  channels?: string[];
  children?: BVHNode[];
}

// True for BVH joints that carry exactly one Xposition channel; these drive morph target weights, not scene transforms.
function isMorphJoint(node: BVHNode): boolean {
  if (node.type === 'ENDSITE') return false;
  return node.channels?.length === 1 && node.channels[0] === 'Xposition';
}

interface BVHFrame {
  time: number;
  position: Vector3;
  rotation: Quaternion;
}

export interface BVHResult {
  skeleton: Skeleton;
  clip: AnimationClip;
}

/**
 * BVHLoader - reads BVH files and outputs a single Skeleton and an AnimationClip
 *
 * Currently only supports bvh files containing a single root.
 */
class BVHLoader extends Loader {
  animateBonePositions: boolean;
  animateBoneRotations: boolean;
  rotationOffsets?: RotationOffsets;

  /** Raw BVH text, keyed by source URL. Allows instant re-parse when only offsets change. */
  private _bvhTextCache: Record<string, string> = {};

  /** Most recently generated BVHResults, keyed by URL. */
  private _resultCache: Record<string, BVHResult> = {};

  /** The URL that was handled most recently. */
  private _lastUrl?: string;

  constructor(manager?: LoadingManager) {
    super(manager);

    this.animateBonePositions = true;
    this.animateBoneRotations = true;
    this.rotationOffsets = undefined;
  }

  private assertLooksLikeBvh(text: string, url: string): void {
    const trimmed = text.trimStart();
    if (trimmed.startsWith('HIERARCHY')) return;

    const preview = trimmed.slice(0, 180).replace(/\s+/g, ' ');
    throw new Error(
      `BVHLoader received non-BVH content for URL "${url}". ` +
      `Response starts with: "${preview}"`,
    );
  }

  load(
    url: string,
    onLoad: (result: BVHResult) => void,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (event: ErrorEvent) => void,
  ): void {
    const loader = new FileLoader(this.manager);
    loader.setPath(this.path);
    loader.setRequestHeader(this.requestHeader);
    loader.setWithCredentials(this.withCredentials);

    if (this._bvhTextCache[url]) {
      const result = this.parse(this._bvhTextCache[url]);
      this._resultCache[url] = result;
      this._lastUrl = url;
      onLoad(result);
      return;
    }

    loader.load(
      url,
      (text) => {
        if (typeof text !== 'string') {
          throw new Error('Expected text to be a string');
        }
        try {
          this.assertLooksLikeBvh(text, url);
          this._bvhTextCache[url] = text;
          const result = this.parse(text);
          this._resultCache[url] = result;
          this._lastUrl = url;
          onLoad(result);
        } catch (e) {
          if (onError) {
            onError(e as ErrorEvent);
          } else {
            console.error(e);
          }
          this.manager.itemError(url);
        }
      },
      onProgress,
      onError,
    );
  }

  parse(text: string): BVHResult {
    function readBvh(
      lines: string[],
      rotationOffsets?: RotationOffsets,
    ): { bones: BVHNode[]; frameTime: number } {
      if (nextLine(lines) !== 'HIERARCHY') {
        console.error('THREE.BVHLoader: HIERARCHY expected.');
      }

      const list: BVHNode[] = [];
      const root = readNode(lines, nextLine(lines), list);

      if (nextLine(lines) !== 'MOTION') {
        console.error('THREE.BVHLoader: MOTION expected.');
      }

      let tokens = nextLine(lines).split(/[\s]+/);
      const numFrames = parseInt(tokens[1]);

      if (isNaN(numFrames)) {
        console.error('THREE.BVHLoader: Failed to read number of frames.');
      }

      tokens = nextLine(lines).split(/[\s]+/);
      let frameTime = parseFloat(tokens[2]);

      const frameRate = Math.round(1 / frameTime);
      frameTime = 1 / frameRate;

      if (isNaN(frameTime)) {
        console.error('THREE.BVHLoader: Failed to read frame time.');
      }

      for (let i = 0; i < numFrames; i++) {
        tokens = nextLine(lines).split(/[\s]+/);
        readFrameData(tokens, i * frameTime, root, rotationOffsets);
      }

      return { bones: list, frameTime };
    }

    function readFrameData(
      data: string[],
      frameTime: number,
      bone: BVHNode,
      rotationOffsets?: RotationOffsets,
    ): void {
      if (bone.type === 'ENDSITE') return;

      const keyframe: BVHFrame = {
        time: frameTime,
        position: new Vector3(),
        rotation: new Quaternion(),
      };

      bone.frames.push(keyframe);

      const quat = new Quaternion();
      const vx = new Vector3(1, 0, 0);
      const vy = new Vector3(0, 1, 0);
      const vz = new Vector3(0, 0, 1);

      const offsets =
        rotationOffsets && bone.name
          ? rotationOffsets[bone.name as keyof RotationOffsets]
          : undefined;

      if (!bone.channels) return;

      for (let i = 0; i < bone.channels.length; i++) {
        const valueStr = data.shift() || '';
        let value = parseFloat(valueStr.trim());

        if (offsets) {
          switch (bone.channels[i]) {
            case 'Xrotation':
              value += offsets[0] || 0;
              break;
            case 'Yrotation':
              value += offsets[1] || 0;
              break;
            case 'Zrotation':
              value += offsets[2] || 0;
              break;
          }
        }

        switch (bone.channels[i]) {
          case 'Xposition':
            keyframe.position.x = value !== undefined ? value : 0;
            break;
          case 'Yposition':
            keyframe.position.y = value;
            break;
          case 'Zposition':
            keyframe.position.z = value;
            break;
          case 'Xrotation':
            quat.setFromAxisAngle(vx, (value * Math.PI) / 180);
            keyframe.rotation.multiply(quat);
            break;
          case 'Yrotation':
            quat.setFromAxisAngle(vy, (value * Math.PI) / 180);
            keyframe.rotation.multiply(quat);
            break;
          case 'Zrotation':
            quat.setFromAxisAngle(vz, (value * Math.PI) / 180);
            keyframe.rotation.multiply(quat);
            break;
          default:
            console.warn('THREE.BVHLoader: Invalid channel type.');
        }
      }

      // Flip sign if needed so we always take the short arc
      if (bone.frames.length > 1) {
        const previousRotation = bone.frames[bone.frames.length - 2].rotation;
        if (previousRotation.dot(keyframe.rotation) < 0) {
          keyframe.rotation.set(
            -keyframe.rotation.x,
            -keyframe.rotation.y,
            -keyframe.rotation.z,
            -keyframe.rotation.w,
          );
        }
      }

      for (let i = 0; bone.children && i < bone.children.length; i++) {
        readFrameData(data, frameTime, bone.children[i], rotationOffsets);
      }
    }

    function readNode(
      lines: string[],
      firstline: string,
      list: BVHNode[],
    ): BVHNode {
      const node: BVHNode = { name: '', type: '', frames: [] };
      list.push(node);

      let tokens = firstline.split(/[\s]+/);

      if (
        tokens[0].toUpperCase() === 'END' &&
        tokens[1].toUpperCase() === 'SITE'
      ) {
        node.type = 'ENDSITE';
        node.name = 'ENDSITE';
      } else {
        // Joint names can contain spaces ("Bip001 Pelvis", "root ground"), which
        // some exporters write verbatim. Taking only the first token truncated
        // every such joint to a duplicate stem, so they could not be matched
        // against the character and the rig stayed static apart from its
        // single-token bones. Keep the whole remainder of the line as the name.
        node.name = firstline.trim().slice(tokens[0].length).trim();
        node.type = tokens[0].toUpperCase();
      }

      if (nextLine(lines) !== '{') {
        console.error('THREE.BVHLoader: Expected opening { after type & name');
      }

      tokens = nextLine(lines).split(/[\s]+/);

      if (tokens[0] !== 'OFFSET') {
        console.error('THREE.BVHLoader: Expected OFFSET but got: ' + tokens[0]);
      }

      if (tokens.length !== 4) {
        console.error('THREE.BVHLoader: Invalid number of values for OFFSET.');
      }

      const offset = new Vector3(
        parseFloat(tokens[1]),
        parseFloat(tokens[2]),
        parseFloat(tokens[3]),
      );

      if (isNaN(offset.x) || isNaN(offset.y) || isNaN(offset.z)) {
        console.error('THREE.BVHLoader: Invalid values of OFFSET.');
      }

      node.offset = offset;

      if (node.type !== 'ENDSITE') {
        tokens = nextLine(lines).split(/[\s]+/);

        if (tokens[0] !== 'CHANNELS') {
          console.error('THREE.BVHLoader: Expected CHANNELS definition.');
        }

        const numChannels = parseInt(tokens[1]);
        node.channels = tokens.splice(2, numChannels);
        node.children = [];
      }

      while (lines.length > 0) {
        const line = nextLine(lines);

        if (line === '}') {
          return node;
        } else {
          if (node.children) {
            node.children.push(readNode(lines, line, list));
          }
        }
      }
      return node;
    }

    function toTHREEBone(source: BVHNode, list: Bone[]): Bone {
      const bone = new Bone();
      list.push(bone);

      if (source.offset) {
        bone.position.add(source.offset);
      }
      const rawJointName = source.name;
      bone.name = sanitizeBindingNodeName(rawJointName);
      if (rawJointName && rawJointName !== bone.name) {
        bone.userData.name = rawJointName;
      }

      if (source.type !== 'ENDSITE') {
        for (let i = 0; source.children && i < source.children.length; i++) {
          bone.add(toTHREEBone(source.children[i], list));
        }
      }

      return bone;
    }

    const toTHREEAnimation = (
      bones: BVHNode[],
      frameTime: number,
    ): AnimationClip => {
      const tracks = [];

      let maxDuration = 0;
      for (let i = 0; i < bones.length; i++) {
        const bone = bones[i];

        if (bone.type === 'ENDSITE') continue;

        const times = [];
        const positions = [];
        const rotations = [];

        for (let j = 0; j < bone.frames.length; j++) {
          const frame = bone.frames[j];

          times.push(frame.time);

          // Cartwheel motions have the offsets baked into the motion, so we don't need to apply them here
          // NOTE this is different than default BVHLoader, which applies the offsets here like this:
          // We intentionally removed the offsets to help ground our characters
          // positions.push(frame.position.x + (bone.offset?.x || 0));
          // positions.push(frame.position.y + (bone.offset?.y || 0));
          // positions.push(frame.position.z + (bone.offset?.z || 0));
          positions.push(frame.position.x);
          positions.push(frame.position.y);
          positions.push(frame.position.z);

          rotations.push(frame.rotation.x);
          rotations.push(frame.rotation.y);
          rotations.push(frame.rotation.z);
          rotations.push(frame.rotation.w);
        }

        const finalDuration = times.at(-1)! + frameTime;
        maxDuration = Math.max(maxDuration, finalDuration);

        const safeName = sanitizeBindingNodeName(bone.name);

        if (this.animateBonePositions) {
          tracks.push(
            new VectorKeyframeTrack(safeName + '.position', times, positions),
          );
        }

        if (this.animateBoneRotations) {
          tracks.push(
            new QuaternionKeyframeTrack(
              safeName + '.quaternion',
              times,
              rotations as any,
            ),
          );
        }
      }

      return new AnimationClip(
        'animation',
        maxDuration,
        tracks as any as ThreeKeyframeTrack[],
      );
    };

    function nextLine(lines: string[]): string {
      let line;
      while ((line = (lines.shift() || '').trim()).length === 0);
      return line;
    }

    const lines = text.split(/[\r\n]+/g);
    const { bones, frameTime } = readBvh(lines, this.rotationOffsets);

    const threeBones: Bone[] = [];
    toTHREEBone(bones[0], threeBones);

    const threeClip = toTHREEAnimation(bones, frameTime);

    const morphJoints = new Set<string>();
    for (const node of bones) {
      if (isMorphJoint(node)) {
        morphJoints.add(sanitizeBindingNodeName(node.name));
      }
    }

    let primaryPositionTrackName = computePrimaryPositionTrackNameFromTracks(
      threeClip.tracks as KeyframeTrack[],
      morphJoints,
    );
    const hasBodyWorldBone = threeBones.some(
      (bone) => bone.name === 'body_world',
    );
    const hasBodyWorldPositionTrack = (threeClip.tracks as KeyframeTrack[]).some(
      (track) => track.name === MHR_BODY_WORLD_POSITION_TRACK_NAME,
    );
    if (hasBodyWorldBone && hasBodyWorldPositionTrack) {
      primaryPositionTrackName = MHR_BODY_WORLD_POSITION_TRACK_NAME;
    }

    const morphTracks = (threeClip.tracks as KeyframeTrack[]).filter((t) =>
      isMorphTrack(t.name, morphJoints),
    );

    const clip = threeClip as AnimationClip & {
      userData?: Record<string, unknown>;
    };
    clip.userData = clip.userData || {};
    clip.userData.morphJoints = morphJoints;
    clip.userData.morphTracks = morphTracks;
    if (primaryPositionTrackName != null) {
      clip.userData.primaryPositionTrackName = primaryPositionTrackName;
    }

    return {
      skeleton: new Skeleton(threeBones),
      clip: threeClip,
    };
  }

  /**
   * Update rotation offsets and re-parse if the file was already loaded.
   */
  updateRotationOffsets(
    rotationOffsets: RotationOffsets,
    url?: string,
  ): BVHResult | undefined {
    this.rotationOffsets = rotationOffsets;

    const targetUrl = url || this._lastUrl;

    if (!targetUrl) return undefined;

    const cachedText = this._bvhTextCache[targetUrl];

    if (!cachedText) {
      return undefined;
    }

    const result = this.parse(cachedText);
    this._resultCache[targetUrl] = result;

    return result;
  }
}

export { BVHLoader };
